import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  Messaging,
  MessagePayload,
} from "firebase/messaging";

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

const firebaseConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "",
};

const isFirebaseConfigValid = () => {
  const isValid = !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId
  );

  return isValid;
};

let app;
let messaging: Messaging | null = null;

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data.type === "NAVIGATE_TO")
      window.location.href = event.data.url;
  });
}

if (isFirebaseConfigValid()) {
  localStorage.setItem("firebaseConfig", JSON.stringify(firebaseConfig));

  if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    try {
      const encodedConfig = encodeURIComponent(JSON.stringify(firebaseConfig));

      navigator.serviceWorker
        .getRegistration("/firebase-messaging-sw.js")
        .then((registration) => {
          if (registration) {
            if (registration.active) {
              registration.active.postMessage({
                type: "PING",
                config: firebaseConfig,
              });
            }
          } else {
            navigator.serviceWorker
              .register(`/firebase-messaging-sw.js?config=${encodedConfig}`)
              .then((registration) => {
                if (registration.installing)
                  registration.installing.postMessage({
                    type: "SKIP_WAITING",
                    config: firebaseConfig,
                  });
              });
          }
        });
    } catch (e) {
      console.error("Error registering service worker:", e);
    }
  }

  app = initializeApp(firebaseConfig);
  messaging = getMessaging(app);
}

const requestFCMPermission = async (): Promise<string | null> => {
  try {
    if (
      !messaging ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator)
    )
      return null;

    if (!navigator.serviceWorker.controller) {
      try {
        await navigator.serviceWorker.ready;
      } catch (e) {
        console.error("Service worker not ready:", e);
      }
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      try {
        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration:
            await navigator.serviceWorker.getRegistration(
              "/firebase-messaging-sw.js",
            ),
        });

        return token ? token : null;
      } catch (tokenError) {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

const onFCMMessageReceived = (
  callback: (payload: MessagePayload) => void,
): (() => void) | null => {
  if (!messaging) return null;
  return onMessage(messaging, callback);
};

export { onFCMMessageReceived, requestFCMPermission, isFirebaseConfigValid };
